import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/kit';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { isOne } from '../plural';
import appTrackerCopy from '@/i18n/ns/app-tracker';
import {
  coverageCounts,
  lineSegments,
  isChoosable,
  validateSchedule,
  type ChartPoint,
  type CreditRanges,
  type FirstRun,
  type ScheduleDraft,
  type SourceCollection,
  type TrackerHistory,
  type TrackerSource,
} from './tracker-model';
import './tracker.css';

/* --------------------------------------------------------------- source step */
export function SourceStep(props: {
  sources: readonly TrackerSource[];
  onChoose: (sourceId: string, chosen: boolean) => void;
  onRequest: (sourceId: string) => void;
  onConnect: (sourceId: string) => void;
  onAskOwner: (sourceId: string) => void;
}) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const locale = useAppCatalogLocale();
  const counts = coverageCounts(props.sources);
  return (
    <section className="pc-tracker-sources" data-testid="tracker-sources">
      <h3>{copy.sourcesHeading}</h3>
      <p className="pc-tracker-note" data-testid="tracker-sources-note">
        {copy.sourcesNote}
      </p>
      <ul>
        {props.sources.map((source) => {
          const status = source.status;
          return (
            <li key={source.id} data-testid="tracker-source" data-kind={status.kind}>
              {isChoosable(source) ? (
                <label className="pc-tracker-choice">
                  <input
                    type="checkbox"
                    data-testid="tracker-choose"
                    aria-label={formatCopy(copy.chooseSource, { name: source.name })}
                    checked={source.chosen}
                    onChange={(event) => props.onChoose(source.id, event.target.checked)}
                  />
                  <strong>{source.name}</strong>
                </label>
              ) : (
                <strong>{source.name}</strong>
              )}
              {status.kind === 'available' && (
                <small className="pc-tracker-gap" data-testid="tracker-reads">
                  {formatCopy(copy.readsLine, { fields: source.fields })}
                </small>
              )}
              {status.kind === 'partial' && (
                <small className="pc-tracker-gap" data-testid="tracker-gap">
                  {formatCopy(copy.partialLine, {
                    fields: source.fields,
                    gaps: status.gaps.join(', '),
                  })}
                </small>
              )}
              {status.kind === 'awaiting_support' && (
                <>
                  <small data-testid="tracker-awaiting">
                    {status.requestedOn
                      ? formatCopy(copy.requestedLine, { date: status.requestedOn })
                      : copy.awaitingLine}
                  </small>
                  {!status.requestedOn && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="tracker-request"
                      onClick={() => props.onRequest(source.id)}
                    >
                      {copy.requestIt}
                    </Button>
                  )}
                </>
              )}
              {status.kind === 'needs_connection' && (
                <>
                  <small data-testid="tracker-needs-connection">
                    {formatCopy(copy.needsConnection, {
                      whose: status.whose,
                      system: status.system,
                    })}
                  </small>
                  {/* Connect only when it would grant the read; otherwise the way forward is the owner. */}
                  {status.canConnect ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="tracker-connect"
                      onClick={() => props.onConnect(source.id)}
                    >
                      {formatCopy(copy.connect, { system: status.system })}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="tracker-ask-owner"
                      onClick={() => props.onAskOwner(source.id)}
                    >
                      {formatCopy(copy.askOwner, { owner: status.owner })}
                    </Button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
      {counts.willJoin > 0 && (
        <p className="pc-tracker-note" data-testid="tracker-coverage-summary">
          {formatCopy(
            isOne(locale, counts.collectNow) ? copy.coverageSummaryOne : copy.coverageSummary,
            {
              n: String(counts.collectNow),
              m: String(counts.willJoin),
            },
          )}
        </p>
      )}
    </section>
  );
}

/* ----------------------------------------------------------- schedule + budget */
export function ScheduleForm(props: {
  initial: ScheduleDraft;
  /** The server's whole-credit bounds for each limit (D74: credits only, never money). */
  limits: CreditRanges;
  timezones: readonly string[];
  saving: boolean;
  saved: ScheduleDraft | null;
  onSave: (draft: ScheduleDraft) => void;
  /**
   * Only the two limits: no time or zone field, no daily-run line, saved as a budget. A published
   * App's time is its declaration's, so a form that cannot change it must not look like it can.
   */
  budgetOnly?: boolean;
}) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const locale = useAppCatalogLocale();
  const [draft, setDraft] = useState(props.initial);
  const [touched, setTouched] = useState(false);
  const full = validateSchedule(draft, props.limits);
  const errors = props.budgetOnly ? { ...full.errors, time: undefined } : full.errors;
  const ok = props.budgetOnly ? !errors.perRun && !errors.monthly && !errors.order : full.ok;
  const set = (key: keyof ScheduleDraft, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const show = touched ? errors : {};
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const rangeText = (range: { min: number; max: number }) =>
    formatCopy(copy.errCredits, { min: number(range.min), max: number(range.max) });
  const credits = (text: string) => `${number(Number(text))} ${copy.creditsUnit}`;
  return (
    <section className="pc-tracker-schedule" data-testid="tracker-schedule">
      {!props.budgetOnly && <h3>{copy.scheduleHeading}</h3>}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          if (ok && !props.saving) props.onSave(draft);
        }}
      >
        {!props.budgetOnly && (
          <label>
            {copy.scheduleTime}
            <input
              type="time"
              data-testid="schedule-time"
              value={draft.time}
              aria-invalid={show.time ? 'true' : undefined}
              onChange={(e) => set('time', e.target.value)}
              onInput={(e) => set('time', e.currentTarget.value)}
            />
            {show.time && <small role="alert">{copy.errTime}</small>}
          </label>
        )}
        {!props.budgetOnly && (
          <label>
            {copy.scheduleTimezone}
            <select
              data-testid="schedule-timezone"
              value={draft.timezone}
              onChange={(e) => set('timezone', e.target.value)}
            >
              {props.timezones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          {copy.budgetPerRun} ({copy.creditsUnit})
          <input
            inputMode="numeric"
            data-testid="schedule-per-run"
            value={draft.perRunLimit}
            aria-invalid={show.perRun ? 'true' : undefined}
            onChange={(e) => set('perRunLimit', e.target.value)}
            onInput={(e) => set('perRunLimit', e.currentTarget.value)}
          />
          {show.perRun && <small role="alert">{rangeText(props.limits.perRun)}</small>}
        </label>
        <label>
          {copy.budgetMonthly} ({copy.creditsUnit})
          <input
            inputMode="numeric"
            data-testid="schedule-monthly"
            value={draft.monthlyLimit}
            aria-invalid={show.monthly || show.order ? 'true' : undefined}
            onChange={(e) => set('monthlyLimit', e.target.value)}
            onInput={(e) => set('monthlyLimit', e.currentTarget.value)}
          />
          {show.monthly && <small role="alert">{rangeText(props.limits.monthly)}</small>}
          {show.order && !show.monthly && <small role="alert">{copy.errOrder}</small>}
        </label>
        {!props.budgetOnly && <p className="pc-tracker-note">{copy.oneRunPerDay}</p>}
        <Button
          type="submit"
          variant="default"
          size="sm"
          data-testid="schedule-save"
          disabled={props.saving}
        >
          {props.saving
            ? copy.scheduleSaving
            : props.budgetOnly
              ? copy.budgetSave
              : copy.scheduleSave}
        </Button>
      </form>
      {props.saved && (
        <p className="pc-tracker-summary" data-testid="schedule-summary">
          {formatCopy(props.budgetOnly ? copy.budgetSummary : copy.scheduleSummary, {
            time: props.saved.time,
            zone: props.saved.timezone,
            perRun: credits(props.saved.perRunLimit),
            monthly: credits(props.saved.monthlyLimit),
          })}
        </p>
      )}
    </section>
  );
}

/* ----------------------------------------------------- per-source collection (Data tab) */
export function CollectionList(props: {
  items: readonly SourceCollection[];
  onReconnect: (sourceId: string) => void;
  onAskOwner: (sourceId: string) => void;
}) {
  const copy = useNamespaceCopy(appTrackerCopy);
  return (
    <ul className="pc-tracker-collection" data-testid="tracker-collection">
      {props.items.map((item) => {
        const s = item.status;
        return (
          <li key={item.sourceId} data-testid="collection-row" data-kind={s.kind}>
            <strong>{item.name}</strong>
            {s.kind === 'collecting' && (
              <small>
                {s.lastRead && s.rows !== null
                  ? formatCopy(copy.collecting, {
                      time: s.time,
                      last: s.lastRead,
                      rows: String(s.rows),
                    })
                  : formatCopy(copy.collectingNever, { time: s.time })}
              </small>
            )}
            {s.kind === 'awaiting_support' && (
              <small className="pc-tracker-muted">
                {formatCopy(copy.awaitingData, { date: s.requestedOn })}
              </small>
            )}
            {s.kind === 'stopped' && (
              <>
                <small>{formatCopy(copy.stopped, { reason: s.reason })}</small>
                {s.canReconnect ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="collection-reconnect"
                    onClick={() => props.onReconnect(item.sourceId)}
                  >
                    {copy.reconnect}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="collection-ask"
                    onClick={() => props.onAskOwner(item.sourceId)}
                  >
                    {formatCopy(copy.askOwner, { owner: s.owner })}
                  </Button>
                )}
              </>
            )}
            {s.kind === 'skipped_budget' && (
              <small data-testid="collection-skipped">
                <StatusPill tone="warning">
                  {formatCopy(copy.skippedBudget, {
                    skipped: String(s.skipped),
                    total: String(s.total),
                  })}
                </StatusPill>
              </small>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The words for why a period has no data; only the producer's reason is used. */
function gapText(copy: Readonly<Record<string, string>>, reason: ChartPoint['reason']) {
  return reason === 'budget'
    ? copy.gapBudget
    : reason === 'unsupported'
      ? copy.gapUnsupported
      : reason === 'failed'
        ? copy.gapFailed
        : copy.gapMarker;
}

/** A series table: a skipped period is a marked gap with its reason, never a zero. */
export function GapSeries(props: { points: readonly ChartPoint[] }) {
  const copy = useNamespaceCopy(appTrackerCopy);
  return (
    <ol className="pc-tracker-series" data-testid="tracker-series">
      {props.points.map((point) => (
        <li key={point.label} data-gap={point.gap ? 'true' : undefined} data-testid="series-point">
          <span>{point.label}</span>
          <span>
            {point.gap || point.value === null
              ? gapText(copy, point.reason)
              : point.unit
                ? `${point.value} ${point.unit}`
                : point.value}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * The chart view of the same series: the line breaks at a skipped period, a hatched band marks it, and
 * the band's tooltip and the table both carry the reason. A real zero stays on the line.
 */
export function GapChart(props: { points: readonly ChartPoint[] }) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const width = 320;
  const height = 120;
  const pad = 12;
  const n = props.points.length;
  const step = n > 1 ? (width - pad * 2) / (n - 1) : 0;
  const segments = lineSegments(props.points);
  const values = segments.flat().map((p) => p.value);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 1);
  const x = (index: number) => pad + index * step;
  const y = (value: number) =>
    height - pad - ((value - min) / (max - min || 1)) * (height - pad * 2);
  return (
    <svg
      className="pc-tracker-chart"
      data-testid="tracker-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={copy.chartLabel}
    >
      <defs>
        <pattern
          id="pc-tracker-hatch"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="6" className="pc-tracker-hatch-line" />
        </pattern>
      </defs>
      {props.points.map((point, index) =>
        point.gap ? (
          <g key={point.label} data-testid="chart-gap">
            <title>{`${point.label}: ${gapText(copy, point.reason)}`}</title>
            <rect
              x={x(index) - Math.max(step / 2, 6)}
              y={pad / 2}
              width={Math.max(step, 12)}
              height={height - pad}
              fill="url(#pc-tracker-hatch)"
              className="pc-tracker-band"
            />
          </g>
        ) : null,
      )}
      {segments.map((segment) => (
        <polyline
          key={segment[0].index}
          data-testid="chart-segment"
          fill="none"
          className="pc-tracker-line"
          points={segment.map((p) => `${x(p.index)},${y(p.value)}`).join(' ')}
        />
      ))}
      {segments.flat().map((p) => (
        <circle key={p.index} cx={x(p.index)} cy={y(p.value)} r="2.5" className="pc-tracker-dot" />
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------- first run */
export function FirstRunCard(props: {
  run: FirstRun | null;
  paused: boolean;
  onPause: () => void;
  onResume: () => void;
}) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const run = props.run;
  const values = run && {
    read: String(run.readSources),
    unsupported: String(run.notSupported),
    cost: run.cost ?? '',
    next: run.next ?? '',
  };
  return (
    <section className="pc-tracker-run" data-testid="tracker-run">
      {run && values ? (
        <div data-testid="first-run">
          <h3>{copy.firstRunTitle}</h3>
          <p className="pc-tracker-meta">
            {formatCopy(
              run.cost === null
                ? copy.firstRunUnmeasured
                : run.next === null
                  ? copy.firstRunNoNext
                  : copy.firstRun,
              values,
            )}
          </p>
        </div>
      ) : (
        <p data-testid="run-none">{copy.lastRunNone}</p>
      )}
      {props.paused && (
        <span data-testid="run-paused">
          <StatusPill tone="warning">{copy.paused}</StatusPill>
        </span>
      )}
      <div className="pc-tracker-actions">
        {props.paused ? (
          <Button
            type="button"
            variant="default"
            size="sm"
            data-testid="run-resume"
            onClick={() => props.onResume()}
          >
            {copy.resume}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="run-pause"
            onClick={() => props.onPause()}
          >
            {copy.pause}
          </Button>
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------- history */
export function HistoryLine(props: { history: TrackerHistory }) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const h = props.history;
  return (
    <p className="pc-tracker-history" data-testid="tracker-history" data-kind={h.kind}>
      {h.kind === 'from_source'
        ? formatCopy(copy.historyFrom, { source: h.source, since: h.since })
        : formatCopy(copy.historyToday, { date: h.date })}
      {h.kind === 'from_source' && (
        <span data-testid="history-imported-mark">
          {' '}
          <StatusPill>{copy.historyImportedMark}</StatusPill>
        </span>
      )}
    </p>
  );
}
